import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Users, TrendingUp, Shield, ArrowRight, Phone, Mail, FileText, UserCheck, Upload, Clock, ClipboardCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
const Membership = () => {
  return <div className="min-h-screen bg-background">
      <Helmet>
        <title>Become a Member | Natcon Sacco</title>
        <meta name="description" content="Ready to join? Learn about the simple 3-step process, membership benefits, and requirements to become a member of Natcon Sacco today. Apply online!" />
        <meta name="keywords" content="Join Natcon Sacco, how to join a Sacco in Kenya, NCA Sacco, Sacco membership requirements, start saving Kenya" />
        <link rel="canonical" href="/membership" />
      </Helmet>

      <Header />
      
      <main className="pt-20">
        {/* Part 1: Page Header and Compelling Introduction */}
        <section className="py-16 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl 2xl:text-5xl font-bold text-foreground mb-6 leading-tight">
                Join Natcon Sacco: Grow With Our Community
              </h1>
              <div className="text-lg text-muted-foreground leading-relaxed space-y-4">
                <p>Become a part of a trusted and growing financial family. Since 2014, Natcon Sacco has been dedicated to the financial well-being of our members. We've grown from an ambitious group of 30 to a strong community of over 280 members, all committed to mutual growth and prosperity.</p>
                <p>
                  With a member retention rate of <span className="text-primary font-semibold">95%</span>, we are a Sacco that people join and stay with. Here's how you can start your journey with us.
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-4 mt-8">
                <Badge variant="secondary" className="text-sm px-4 py-2">280+ Members<Users className="w-4 h-4 mr-2" />
                  259+ Members
                </Badge>
                <Badge variant="secondary" className="text-sm px-4 py-2">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  95% Retention Rate
                </Badge>
                <Badge variant="secondary" className="text-sm px-4 py-2">
                  <Shield className="w-4 h-4 mr-2" />
                  Since 2014
                </Badge>
              </div>
            </div>
          </div>
        </section>

        {/* Part 2: Your Simple, 3-Step Path to Membership */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-12">
                Your Path to Joining Us
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {/* Step 1 */}
                <div className="text-center relative">
                  <div className="bg-primary text-primary-foreground rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    1
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Confirm Your Eligibility</h3>
                  <p className="text-muted-foreground">
                    Membership is currently open to all employees of the National Construction Authority (NCA). We are actively working on plans to expand our common bond to welcome members from other public sector organizations in the near future.
                  </p>
                  {/* Connector line for desktop */}
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-border -translate-x-1/2 -z-10"></div>
                </div>

                {/* Step 2 */}
                <div className="text-center relative">
                  <div className="bg-primary text-primary-foreground rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    2
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Your Ownership Stake</h3>
                  <p className="text-muted-foreground mb-4">
                    Becoming a member means becoming an owner. Your initial investment secures your stake in the Sacco and is composed of two parts:
                  </p>
                  <div className="space-y-3">
                    <div className="bg-card border rounded-lg p-4">
                      <h4 className="font-medium text-foreground">One-Time Joining Fee</h4>
                      <p className="text-sm text-muted-foreground">A non-refundable fee of <span className="font-semibold text-primary">Kshs. 1,500</span> to process your membership.</p>
                    </div>
                    <div className="bg-card border rounded-lg p-4">
                      <h4 className="font-medium text-foreground">Minimum Share Capital</h4>
                      <p className="text-sm text-muted-foreground">A one-time purchase of <span className="font-semibold text-primary">Kshs. 5,000</span> (250 shares at Kshs. 20 each). This is your ownership stake in the Sacco and earns you dividends.</p>
                    </div>
                  </div>
                  {/* Connector line for desktop */}
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-border -translate-x-1/2 -z-10"></div>
                </div>

                {/* Step 3 */}
                <div className="text-center">
                  <div className="bg-primary text-primary-foreground rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    3
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Complete Your Application</h3>
                  <p className="text-muted-foreground mb-6">
                    Ready to start? Our online application process is quick, easy, and secure. Click the button below to begin.
                  </p>
                  <Button size="lg" className="w-full" asChild>
                    <a href="/membership-application">
                      Apply Online Now
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Detailed Application Instructions */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <Badge variant="outline" className="mb-4 text-primary border-primary">
                  <ClipboardCheck className="w-4 h-4 mr-2" />
                  Step-by-Step Guide
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  How to Complete Your Application
                </h2>
                <p className="text-muted-foreground text-lg">
                  Follow these simple steps to become a Natcon Sacco member. The entire process can be completed online in about 15-20 minutes.
                </p>
              </div>

              <div className="space-y-6">
                {/* Step 1: Prepare Documents */}
                <Card className="border-l-4 border-l-primary">
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-4">
                      <div className="bg-primary text-primary-foreground rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold shrink-0">
                        1
                      </div>
                      <div>
                        <CardTitle className="flex items-center gap-2 text-xl">
                          <FileText className="w-5 h-5 text-primary" />
                          Prepare Your Documents
                        </CardTitle>
                        <CardDescription className="mt-1">Before you begin, gather the following:</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pl-18">
                    <ul className="grid sm:grid-cols-2 gap-3 ml-14">
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                        <span>Copy of your National ID (front & back)</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                        <span>Copy of your KRA PIN certificate</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                        <span>Recent passport-size photograph</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                        <span>Your NCA staff/employee number</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Step 2: Fill Personal Details */}
                <Card className="border-l-4 border-l-primary">
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-4">
                      <div className="bg-primary text-primary-foreground rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold shrink-0">
                        2
                      </div>
                      <div>
                        <CardTitle className="flex items-center gap-2 text-xl">
                          <UserCheck className="w-5 h-5 text-primary" />
                          Fill in Your Personal Details
                        </CardTitle>
                        <CardDescription className="mt-1">Complete all required fields in the application form:</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 ml-14 text-sm text-muted-foreground">
                      <li>• Full names as per your National ID</li>
                      <li>• Contact information (phone & email)</li>
                      <li>• Employment details (staff number, department, duty station)</li>
                      <li>• Your preferred monthly contribution amount (minimum Kshs. 2,000)</li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Step 3: Nominate Beneficiaries */}
                <Card className="border-l-4 border-l-primary">
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-4">
                      <div className="bg-primary text-primary-foreground rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold shrink-0">
                        3
                      </div>
                      <div>
                        <CardTitle className="flex items-center gap-2 text-xl">
                          <Users className="w-5 h-5 text-primary" />
                          Nominate Your Beneficiaries
                        </CardTitle>
                        <CardDescription className="mt-1">Designate who will receive your savings in case of any eventuality:</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 ml-14 text-sm text-muted-foreground">
                      <li>• Provide details for at least one nominee (two recommended)</li>
                      <li>• Include their full name, ID number, and relationship to you</li>
                      <li>• Assign percentage allocation (must total 100%)</li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Step 4: Upload Documents */}
                <Card className="border-l-4 border-l-primary">
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-4">
                      <div className="bg-primary text-primary-foreground rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold shrink-0">
                        4
                      </div>
                      <div>
                        <CardTitle className="flex items-center gap-2 text-xl">
                          <Upload className="w-5 h-5 text-primary" />
                          Upload Your Documents
                        </CardTitle>
                        <CardDescription className="mt-1">Attach the documents you prepared in Step 1:</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="ml-14 text-sm text-muted-foreground space-y-2">
                      <p>• Accepted formats: PDF, JPG, PNG (max 5MB per file)</p>
                      <p>• Ensure documents are clear and legible</p>
                      <p className="text-amber-600 font-medium">⚠️ Your application cannot be processed without valid documents</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Step 5: Review & Submit */}
                <Card className="border-l-4 border-l-green-600">
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-4">
                      <div className="bg-green-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold shrink-0">
                        5
                      </div>
                      <div>
                        <CardTitle className="flex items-center gap-2 text-xl">
                          <ClipboardCheck className="w-5 h-5 text-green-600" />
                          Review, Agree & Submit
                        </CardTitle>
                        <CardDescription className="mt-1">Finalize your application:</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 ml-14 text-sm text-muted-foreground">
                      <li>• Review all information for accuracy</li>
                      <li>• Read and accept the terms and conditions</li>
                      <li>• Click <strong className="text-foreground">"Submit Membership Application"</strong></li>
                    </ul>
                  </CardContent>
                </Card>

                {/* What Happens Next */}
                <Card className="bg-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Clock className="w-5 h-5 text-primary" />
                      What Happens After You Submit?
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="flex items-start gap-3">
                      <div className="bg-primary/10 rounded-full p-1">
                        <CheckCircle className="w-4 h-4 text-primary" />
                      </div>
                      <p><strong>Confirmation:</strong> You'll receive an email confirming your application was received.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-primary/10 rounded-full p-1">
                        <CheckCircle className="w-4 h-4 text-primary" />
                      </div>
                      <p><strong>Review:</strong> Our Management Committee reviews your application (typically within 7 working days).</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-primary/10 rounded-full p-1">
                        <CheckCircle className="w-4 h-4 text-primary" />
                      </div>
                      <p><strong>Payment:</strong> Upon approval, you'll be guided on how to make your initial payments (joining fee + share capital).</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-primary/10 rounded-full p-1">
                        <CheckCircle className="w-4 h-4 text-primary" />
                      </div>
                      <p><strong>Welcome:</strong> Once payment is confirmed, you officially become a Natcon Sacco member!</p>
                    </div>
                  </CardContent>
                </Card>

                {/* CTA Button */}
                <div className="text-center pt-4">
                  <Button size="lg" className="text-lg px-8" asChild>
                    <a href="/membership-application">
                      Start Your Application Now
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Part 3: The Benefits of Being a Natcon Sacco Member */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-12">
                Why Join Us? The Natcon Advantage
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {/* Card 1 */}
                <Card className="h-full">
                  <CardHeader className="text-center">
                    <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl">Build Your Savings & Earn Returns</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-center">
                      Grow your wealth through consistent monthly deposits. As a member, you earn competitive interest on your deposits and annual dividends on your share capital, ensuring your money works for you.
                    </CardDescription>
                  </CardContent>
                </Card>

                {/* Card 2 */}
                <Card className="h-full">
                  <CardHeader className="text-center">
                    <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl">Access Loans to Achieve Your Goals</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-center">
                      After just six months of membership, you become eligible for our range of affordable loan products. Are you transferring from another Sacco? You qualify for a loan immediately! Take advantage of our Emergency, Development, and School Fees loans.
                    </CardDescription>
                  </CardContent>
                </Card>

                {/* Card 3 */}
                <Card className="h-full">
                  <CardHeader className="text-center">
                    <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl">Join a Strong & Supportive Community</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-center">
                      You're not just a customer; you're a member-owner. We pride ourselves on our strong sense of community, member-focused service, and transparent governance led by our dedicated Board of Directors.
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Part 4: Mini FAQ for This Page */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-12">
                Quick Answers
              </h2>
              
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1">
                  <AccordionTrigger className="text-left">
                    What is the difference between my Deposits and my Share Capital?
                  </AccordionTrigger>
                  <AccordionContent>
                    Your Deposits are your non-withdrawable savings which earn you interest and determine your loan eligibility. Your Share Capital is your non-refundable ownership stake in the Sacco which earns you dividends.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-2">
                  <AccordionTrigger className="text-left">
                    How soon can I take a loan after joining?
                  </AccordionTrigger>
                  <AccordionContent>
                    You are eligible for a loan after six months. However, if you are transferring your membership from another Sacco, you qualify immediately.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-3">
                  <AccordionTrigger className="text-left">
                    Who runs the Sacco?
                  </AccordionTrigger>
                  <AccordionContent>
                    The Sacco is managed by a democratically elected Board of Directors and a Supervisory Committee who are accountable to the members.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </section>

        {/* Part 5: Final Call-to-Action */}
        <section className="py-16 bg-gradient-to-r from-primary to-primary/80">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-6">
                Your Journey to Financial Empowerment Starts Here
              </h2>
              <div className="space-y-6">
                <Button size="lg" variant="secondary" className="text-lg px-8 py-3" asChild>
                  <a href="/membership-application">
                    Start My Application
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </a>
                </Button>
                <div className="text-primary-foreground/80">
                  <p className="mb-2">Have More Questions?</p>
                  <div className="flex flex-wrap justify-center gap-6 text-sm">
                    <a href="/contact" className="hover:text-primary-foreground transition-colors flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Contact Our Support Team
                    </a>
                    <a href="mailto:natconsacco@nca.go.ke" className="hover:text-primary-foreground transition-colors flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      natconsacco@nca.go.ke
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>;
};
export default Membership;